// src/hooks/useSecureAuth.ts
import { useEffect, useState, useRef } from "react";
import EndPoints from "../config/EndPoint";
import {
  clearClientAuthState,
  clearLegacyAuthTokens,
  setSessionUser,
  subscribeAuthEvents,
} from "./authSession";
import { refreshSession } from "../hooks/useFetch";

/**
 * Verifica sesión vía cookies HttpOnly + GET /users/me/.
 * No lee ni parsea JWT en el cliente.
 */
export const useSecureAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const isVerifyingRef = useRef(false);

  useEffect(() => {
    if (isVerifyingRef.current) return;

    const verifyAuth = async () => {
      isVerifyingRef.current = true;

      try {
        // Limpiar tokens legacy que pudieran quedar de versiones anteriores.
        clearLegacyAuthTokens();

        let res = await fetch(EndPoints.USER.ME, {
          method: "GET",
          credentials: "include",
          headers: { Accept: "application/json" },
        });

        if (res.status === 401) {
          const refreshed = await refreshSession();
          if (refreshed) {
            res = await fetch(EndPoints.USER.ME, {
              method: "GET",
              credentials: "include",
              headers: { Accept: "application/json" },
            });
          }
        }

        if (res.ok) {
          const payload = await res.json().catch(() => null);
          const user = payload?.data?.user || payload?.user;
          if (user) setSessionUser(user);
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
          if (res.status === 401) {
            clearClientAuthState();
          }
        }
      } catch (error) {
        console.warn("Error de red al verificar sesión:", error);
        // No forzar logout ante fallo de red temporal.
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
        isVerifyingRef.current = false;
      }
    };

    verifyAuth();

    const unsub = subscribeAuthEvents((type) => {
      if (type === "logout") {
        setIsAuthenticated(false);
      }
      if (type === "login") {
        setIsAuthenticated(true);
      }
    });

    return () => unsub();
  }, []);

  return { isAuthenticated, loading };
};
